import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Magnetic } from "./_shared";

export default function HeroSection({ hero }) {
  const heroPre = hero.pre_header ?? "ASVASA · MMXXVI";
  const heroH1 = hero.headline_part1 ?? "Tradition,";
  const heroH2 = hero.headline_part2 ?? "reimagined for 2026.";
  const heroSub =
    hero.subheadline ??
    "Bridging the gap between artisanal heritage and the needs of the modern man with our breathable summer edit.";
  const heroBanner =
    hero.banner_image_url ??
    "https://customer-assets.emergentagent.com/job_asvasa-premium/artifacts/71c63goh_hf_20260426_042859_317afea6-3549-4202-beb8-4e720c3b1b74%20%281%29.png";
  const ctaPrimaryLabel = hero.cta_primary_label ?? "Shop New Arrivals";
  const ctaPrimaryHref = hero.cta_primary_href ?? "/shop?sort=newest";
  const ctaSecondaryLabel = hero.cta_secondary_label ?? "Explore Our Craft";
  const ctaSecondaryHref = hero.cta_secondary_href ?? "/blog";

  return (
    <section
      className="relative h-[88vh] min-h-[560px] sm:min-h-[640px] md:min-h-[680px] md:h-[94vh] overflow-hidden paper-grain"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={heroBanner}
          alt="Asvasa festive banner"
          className="absolute inset-0 w-full h-full object-cover ken-burns image-in"
          style={{ objectPosition: "65% center" }}
        />
      </div>
      <div className="absolute inset-y-0 left-0 w-[48%] bg-gradient-to-r from-[#1A1A1A]/35 via-[#1A1A1A]/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1A1A1A]/40 to-transparent" />

      <div
        className="absolute top-12 right-8 md:right-16 float-soft hidden md:block opacity-50"
        aria-hidden="true"
      >
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
          <path
            d="M40 8c-12 10-18 20-18 30 0 12 8 20 18 20s18-8 18-20c0-10-6-20-18-30z"
            stroke="#FDFAF6"
            strokeWidth="0.8"
            fill="none"
          />
          <circle cx="40" cy="38" r="4" stroke="#FDFAF6" strokeWidth="0.8" fill="none" />
        </svg>
      </div>

      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-end pb-8 sm:pb-12 md:pb-20">
        <div className="w-full max-w-[20rem] sm:max-w-md md:max-w-[28rem] bg-[#c3ba9e] p-5 sm:p-7 md:p-9 border-l-4 border-[#1A1A1A] shadow-2xl">
          <p className="eyebrow text-[#1A1A1A] word-up" style={{ animationDelay: "200ms" }}>
            {heroPre}
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[0.98] mt-3 text-[#1A1A1A]">
            <span className="word-up inline-block" style={{ animationDelay: "350ms" }}>
              {heroH1}
            </span>{" "}
            <em className="italic">
              <span className="word-up inline-block" style={{ animationDelay: "500ms" }}>
                {heroH2}
              </span>
            </em>
          </h1>
          <p
            className="text-[#1A1A1A]/80 mt-4 sm:mt-5 text-[13px] sm:text-[14px] md:text-[15px] leading-relaxed word-up"
            style={{ animationDelay: "950ms" }}
          >
            {heroSub}
          </p>
          <div
            className="mt-5 sm:mt-6 flex flex-wrap gap-2 sm:gap-3 word-up"
            style={{ animationDelay: "1150ms" }}
          >
            <Magnetic>
              <Link to={ctaPrimaryHref}>
                <Button
                  className="bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none px-5 sm:px-7 py-4 sm:py-5 tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[10px] sm:text-xs btn-shimmer"
                  data-testid="hero-shop-cta"
                >
                  {ctaPrimaryLabel}
                </Button>
              </Link>
            </Magnetic>
            <Link to={ctaSecondaryHref}>
              <Button
                variant="outline"
                className="border-[#1A1A1A] text-[#1A1A1A] bg-transparent hover:bg-[#1A1A1A] hover:text-[#c3ba9e] rounded-none px-5 sm:px-7 py-4 sm:py-5 tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[10px] sm:text-xs"
              >
                {ctaSecondaryLabel}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-6 right-8 hidden md:flex flex-col items-center gap-2 text-[#FDFAF6]/85"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.32em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-[#FDFAF6]/60 animate-pulse" />
      </div>
    </section>
  );
}
